import { useState, useCallback } from "react";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ACTIVE, STATUS_HEX, ageFlag, daysSince, heldFromStatus, fmtTime } from "@/lib/status";
import PageHeader from "@/components/PageHeader";
import AdvanceButton from "@/components/AdvanceButton";
import HoldReasonDialog from "@/components/HoldReasonDialog";
import { PriorityBadge, TypeBadge, AgeBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import KioskBoard from "@/pages/KioskBoard";
import { RefreshCw, PauseCircle } from "lucide-react";
import { toast } from "sonner";

const HOLD_ELIGIBLE = ["Pending", "Optimising", "At Machine", "In Rework Rack"];

function Card({ r, canHold, onHold, onUpdated }) {
  const flag = ageFlag(r);
  const heldFrom = heldFromStatus(r);
  return (
    <div data-testid={`board-card-${r.id}`} className={`bg-white border rounded-sm p-4 space-y-3 shadow-sm ${flag?.level === "overdue" ? "border-red-300" : flag ? "border-amber-300" : "border-[#e6e5df]"}`}>
      <div className="flex items-start justify-between gap-2">
        <Link to={`/rework/${r.id}`} className="min-w-0" data-testid={`board-link-${r.id}`}>
          <div className="mono text-lg font-medium text-[#232826] leading-tight">{r.job_number}</div>
          <div className="mono text-sm text-[#424e43]">{r.part_number}</div>
          {r.part_name && <div data-testid={`board-part-name-${r.id}`} className="text-sm font-semibold text-[#232826] leading-tight mt-0.5">{r.part_name}</div>}
        </Link>
        <div className="flex flex-col items-end gap-1">
          <PriorityBadge priority={r.priority} />
          <TypeBadge type={r.request_type} />
        </div>
      </div>
      <div className="text-xs text-[#6b6a68] line-clamp-2">{r.rework_reason} — {r.description}</div>
      {r.status === "On Hold" && (
        <div
          data-testid={`board-hold-context-${r.id}`}
          className="space-y-1 border border-orange-200 bg-orange-50 px-2 py-1.5 text-xs text-orange-900"
        >
          {r.hold_reason && <div>Hold: {r.hold_reason}</div>}
          <div className="font-medium">Paused at: {heldFrom || "Not recorded"}</div>
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] text-[#a39f98] font-mono">
        <span>{daysSince(r.date_submitted)}d open{r.assigned_to ? ` · ${r.assigned_to}` : ""}</span>
        <AgeBadge flag={flag} />
      </div>
      <div className="space-y-2 pt-1">
        <AdvanceButton
          rework={r}
          onDone={onUpdated}
          className="h-auto min-h-8 w-full whitespace-normal py-1 leading-tight"
        />
        {canHold && HOLD_ELIGIBLE.includes(r.status) && (
          <button
            onClick={() => onHold(r)}
            data-testid={`board-hold-${r.id}`}
            className="flex h-8 w-full items-center justify-center gap-1 rounded-sm border border-orange-300 bg-orange-50 px-2.5 text-[11px] uppercase tracking-widest text-orange-700 hover:bg-orange-100"
          >
            <PauseCircle size={12} /> Hold
          </button>
        )}
      </div>
    </div>
  );
}

export default function Board() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tick, setTick] = useState(new Date());
  const [holdTarget, setHoldTarget] = useState(null);
  const canHold = user?.role === "admin" || user?.role === "lead";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reworks", { params: { active_only: true } });
      setRows(data);
      setTick(new Date());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load board");
    }
  }, []);

  useAutoRefresh(load);

  const onUpdated = (data) => setRows((rs) => rs.map((r) => (r.id === data.id ? data : r)).filter((r) => ACTIVE.includes(r.status)));

  const confirmHold = async (reason) => {
    try {
      const { data } = await api.patch(`/reworks/${holdTarget.id}`, { status: "On Hold", hold_reason: reason });
      onUpdated(data);
      toast.success("Placed on hold");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setHoldTarget(null);
    }
  };

  const prioRank = { Urgent: 0, Normal: 1, Low: 2 };
  const sorted = (list) => [...list].sort((a, b) => (prioRank[a.priority] ?? 1) - (prioRank[b.priority] ?? 1) || new Date(a.date_submitted) - new Date(b.date_submitted));

  if (user?.role === "kiosk") {
    return <KioskBoard />;
  }

  return (
    <div className="min-h-screen">
      <PageHeader title="Rework Board" subtitle="Live factory view. Tap a card to move it to the next stage." testid="board-header"
        actions={
          <button onClick={load} data-testid="board-refresh" className="flex items-center gap-2 text-xs text-[#6b6a68] hover:text-[#232826] font-mono">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            <span data-testid="board-live-label">Live · {fmtTime(tick)}</span> <RefreshCw size={13} />
          </button>
        } />
      <div className="p-4 md:p-6 overflow-x-auto">
        <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] gap-4 min-w-max lg:min-w-0 lg:grid-flow-row lg:grid-cols-5">
          {ACTIVE.map((st) => {
            const list = sorted(rows.filter((r) => r.status === st));
            return (
              <div key={st} className="bg-[#efece5] rounded-sm flex flex-col min-h-[60vh]" data-testid={`board-col-${st.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="px-4 py-3 flex items-center justify-between border-b-2" style={{ borderColor: STATUS_HEX[st] }}>
                  <div className="text-xs uppercase tracking-widest font-semibold text-[#232826]">{st}</div>
                  <div className="num text-sm text-[#424e43] bg-white px-2 rounded-sm border border-[#e6e5df]" data-testid={`board-count-${st.replace(/\s+/g, "-").toLowerCase()}`}>{list.length}</div>
                </div>
                <div className="p-3 space-y-3 flex-1">
                  {list.length === 0 && <div className="text-xs text-[#a39f98] text-center py-8">Empty</div>}
                  {list.map((r) => <Card key={r.id} r={r} canHold={canHold} onHold={setHoldTarget} onUpdated={onUpdated} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <HoldReasonDialog open={!!holdTarget} onOpenChange={(o) => !o && setHoldTarget(null)} onConfirm={confirmHold} />
    </div>
  );
}
